<script lang="ts">
	import type { ConditionalKeys } from 'type-fest'
	import type { GetFieldEditabilityFn } from './part'
	import type { ComponentProps, Snippet } from 'svelte'
	import type { Mediator, SvelteAsr } from 'types/common'
	import type { PartModel } from 'utility/load-models-for-manufacturer'
	import type { PartScreenStageOne$result, LIB_partStoreData$result, VehicleLookup$result } from '$houdini'
	import type {
		OptionValueMap,
		PartForClient as Part,
		SettingValues,
		TypeField,
		FlatAttachment,
	} from 'utility/load-part'

	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import Button from '@isoftdata/svelte-button'
	import Card from '@isoftdata/svelte-card'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Fieldset from '@isoftdata/svelte-fieldset'
	import HtpInfoButton from 'components/HtpInfoButton.svelte'
	import Icon from '@isoftdata/svelte-icon'
	import ImagePreview from 'components/ImagePreview.svelte'
	import Input from '@isoftdata/svelte-input'
	import Table, { type Column } from '@isoftdata/svelte-table'
	import LoadItemModal from '@isoftdata/svelte-load-item-modal'
	import PartMarketingFields from 'components/PartMarketingFields.svelte'
	import Select from '@isoftdata/svelte-select'
	import TextArea from '@isoftdata/svelte-textarea'

	import QaList from './QaList.svelte'
	import LocationTable from './LocationTable.svelte'

	import { klona } from 'klona'
	import { dequal } from 'dequal'
	import { graphql } from '$houdini'
	import { getContext, tick } from 'svelte'
	import { v4 as uuid } from '@lukeed/uuid'
	import { hasPermission } from 'utility/permission'
	import { getEventValue } from '@isoftdata/browser-event'
	import { localWritable } from '@macfja/svelte-persistent-store'
	import loadVehicleModelsForMake from 'utility/load-models-for-make'
	import loadModelsForManufacturer from 'utility/load-models-for-manufacturer'

	interface Props {
		asr: SvelteAsr
		assyModels: Array<PartModel>
		attachments?: Array<FlatAttachment>
		disabled?: boolean
		glCategories: PartScreenStageOne$result['glCategories']
		inventoryConditions: PartScreenStageOne$result['inventoryConditions']
		inventoryTypeList: PartScreenStageOne$result['inventoryTypeList']
		inventoryTypeData: PartScreenStageOne$result['inventoryTypeList'][number] | null
		optionValueMap: OptionValueMap
		origPart: Part
		part: Part
		typeFields: Array<TypeField>
		/** "True" Models*/
		partModels: Array<PartModel>
		partStoreLocations: LIB_partStoreData$result['locations']
		saleClasses: PartScreenStageOne$result['saleClasses']
		sellPriceClasses: PartScreenStageOne$result['sellPriceClasses']
		settingValues: SettingValues
		userPartStatusList: PartScreenStageOne$result['userPartStatusList']
		vehicleMakes: PartScreenStageOne$result['vehicleMakes']
		vehicleModels: Array<string>
		vendorList: PartScreenStageOne$result['vendors']['items']
		shouldDisable: GetFieldEditabilityFn
		qaChange?: () => void
	}

	let {
		asr,
		assyModels = $bindable(),
		attachments = [],
		disabled = false,
		glCategories,
		inventoryConditions,
		inventoryTypeList,
		inventoryTypeData = $bindable(),
		optionValueMap = $bindable(),
		origPart = $bindable(),
		part = $bindable(),
		typeFields = $bindable(),
		partModels = $bindable(),
		partStoreLocations,
		saleClasses,
		sellPriceClasses,
		settingValues,
		userPartStatusList,
		vehicleMakes,
		vehicleModels = $bindable(),
		vendorList,
		shouldDisable,
		qaChange,
	}: Props = $props()

	let coreClassHistoryLoading = $state(false)
	let selectedSerialIndex = $state(-1)
	let trueModelsLoading = $state(false)
	let assyModelsLoading = $state(false)
	let allowVirtualLocations = !settingValues.location.enforceLocationHierarchy

	let vehicleLookupModal: LoadItemModal<VehicleLookup$result['vehicles']['items'][number]> | undefined =
		$state(undefined)
	let qaList: QaList | undefined = $state(undefined)
	let serialQaList: QaList | undefined = $state(undefined)
	let locationTable: LocationTable | undefined = $state(undefined)
	let marketingFields: PartMarketingFields | undefined = $state(undefined)

	let disableGlobalFields = $derived(
		disabled || part.skuType.inventories.some(inventory => !hasPermission('PARTS_CAN_EDIT_INFO', inventory.store.id)),
	)
	let inventoryTypeCategories = $derived(inventoryTypeData?.categories || [])
	let inventoryTrueManufacturers = $derived(inventoryTypeData?.partManufacturers || [])
	let typeSetPartType = $derived(inventoryTypeList.find(type => type.inventoryTypeId === inventoryTypeData?.typeSetId))
	let inventoryAssyManufacturers = $derived(typeSetPartType?.partManufacturers || [])
	let selectedSerialId = $derived(selectedSerialIndex === -1 ? null : part.serials[selectedSerialIndex].id)
	let selectedSerialUuid = $derived(selectedSerialIndex === -1 ? null : part.serials[selectedSerialIndex].uuid)
	let field = $derived(settingValues.inventory.fields)

	// $optionValueMap accesses the value of the store and should make this reactive
	let selectedSerialQuestions = $derived(
		selectedSerialIndex === -1
			? $optionValueMap &&
					part.inventoryOptions.map(option => ({
						...option,
						value: optionValueMap.get({ optionId: option.id }) ?? '',
					}))
			: $optionValueMap &&
					part.inventoryOptions.map(option => ({
						...option,
						value:
							optionValueMap.get({
								optionId: option.id,
								serialId: selectedSerialId,
								serialUuid: selectedSerialUuid,
							}) ?? '',
					})),
	)

	const mediator = getContext<Mediator>('mediator')
	const cardsOpen = localWritable('partScreenCardsOpen', {
		basic: true,
		marketing: true,
		fulfillment: true,
		replenishable: true,
		stocking: true,
		salesAndShipping: true,
		vehicle: true,
	})

	const noneSaleClass = saleClasses.find(saleClass => saleClass.code === 'NONE') ?? null

	const serialTableColumns: Array<Column> = [
		{ property: '_ACTION', icon: 'info-circle', align: 'center', width: '1rem' },
		{ property: 'number', minWidth: '150px', name: 'Serial #', defaultSortColumn: true },
		{ property: 'displayStatus', name: 'Status', width: '1rem' },
		{ property: 'location[name]', minWidth: '150px', name: 'Location' },
		{ property: 'source', name: 'Source', width: '1rem' },
		{ property: 'usedOn', name: 'Used On', width: '1rem' },
	]

	async function refetchQa() {
		// If the list isn't rendered, we have to render it before we can fetch new questions
		const qaCard = part.serialized ? 'stocking' : 'basic'
		if (!$cardsOpen[qaCard]) {
			$cardsOpen[qaCard] = true
			await tick()
		}

		let list = part.serialized ? serialQaList : qaList

		if (part.inventoryTypeId) {
			list?.fetchNewQuestions({
				categoryName: part.category?.name ?? '',
				manufacturerId: part.manufacturer?.id ?? null,
				modelId: part.model?.id ?? null,
				inventoryTypeId: part.inventoryTypeId,
			})
		}
	}

	function useSuggestedQuantities() {
		part.minQuantity = Number(part.suggestedMinQuantity) || 0
		part.maxQuantity = Number(part.suggestedMaxQuantity) || 0
	}

	//#region change handlers
	/** Because we can close the marketing card and un-render those fields,
	 * we have to open the card again to run these functions 🙄 */
	async function updatePricingAndAppraise() {
		const marketingOpen = $cardsOpen.marketing
		if (!$cardsOpen.marketing) {
			$cardsOpen.marketing = true
			await tick()
		}
		if (marketingFields) {
			await Promise.all([marketingFields.updateDefaultPricing(), marketingFields.appraise()])
		} else {
			console.error('Marketing Fields not loaded, skipping appraise and updateDefaultPricing')
		}
		$cardsOpen.marketing = marketingOpen
	}

	function qaInputChange(option: Parameters<ComponentProps<typeof QaList>['qaInputChange']>[0]) {
		if (selectedSerialIndex > -1) {
			optionValueMap.set(
				{
					optionId: option.id,
					serialId: part.serials[selectedSerialIndex].id,
					serialUuid: part.serials[selectedSerialIndex].uuid,
				},
				option.value,
			)
		} else {
			optionValueMap.set({ optionId: option.id }, option.value)
		}
		qaChange?.()
	}

	function replenishableChange() {
		let newStockCategory = part.stockCategory
		let newPublic = part.public

		// The desktop will, when you uncheck 'Replenishable', set the stock category to 'STANDARD' if there's a vehicleId, or 'MISC' if there's not.
		// When you check 'Replenishable', it will always set the stock category to 'REPLENISHABLE'
		if (part.replenishable) {
			newStockCategory = 'REPLENISHABLE'
			newPublic = !!settingValues.inventory.defaultReplenishablePartsArePublic
		} else if (part.vehicleId) {
			newStockCategory = 'STANDARD'
			newPublic = !!settingValues.inventory.defaultNonReplenishablePartsArePublic
		} else {
			newStockCategory = 'MISC'
			newPublic = !!settingValues.inventory.defaultNonReplenishablePartsArePublic
		}

		part.stockCategory = newStockCategory
		part.public = newPublic
		part.saleClass = newPublic ? part.saleClass : noneSaleClass
	}

	function numberChange(event: Event, key: ConditionalKeys<Part, number>) {
		part[key] = Number(getEventValue(event))
	}

	function serializePart(serialize: boolean, serial: string) {
		if (serialize && confirm('Are you sure you want to serialize this part? After saving, this cannot be undone.')) {
			part.serialized = true
			if (!part.serials.length) {
				addSerial(serial, part.locations[0])
			}
		} else if (
			!serialize &&
			(!part.serials.some(serial => serial.number && !serial.deleted) ||
				confirm(
					'Are you sure you want to remove serial numbers from this part? Any unsaved serial information will be discarded.',
				))
		) {
			part.serialized = false
		}
	}

	async function addSerial(number = '', location: (typeof part.locations)[number] | null = null) {
		const newSerialUuid = uuid()
		const optionsWithDefaults = part.inventoryOptions.map(option => {
			const defaultValue = optionValueMap.get({ optionId: option.id }) ?? ''
			if (defaultValue) {
				optionValueMap.set({ optionId: option.id, serialId: null, serialUuid: newSerialUuid }, defaultValue)
			}
			return {
				...option,
				value: defaultValue,
			}
		})
		part.serials.push({
			id: null,
			uuid: newSerialUuid, // need a unique id for new, unsaved serials
			inventoryOptions: optionsWithDefaults,
			location: klona(location),
			number,
			status: 'AVAILABLE',
			displayStatus: 'Available',
			enteredOnDocumentId: null,
			enteredOnDocumentLineId: null,
			enteredOnDocumentStoreId: null,
			enteredOnDocumentType: null,
			usedOnDocumentStoreId: null,
			dateEntered: new Date(),
			usedOnDocumentType: null,
			usedOnDocumentId: null,
			usedOn: 'N/A',
			source: 'N/A',
		})
		part.serials = part.serials
		selectedSerialIndex = part.serials.length - 1
		await tick()
		// If a # if provided, focus the location field, otherwise the serial # field
		document.getElementById(`serial-${number ? 'location' : 'number'}-${selectedSerialIndex}`)?.focus()
	}

	function deleteSerial() {
		if (selectedSerialIndex === -1) {
			return
		}

		// Remove quantity at the serial's location, and delete it if needed
		const serialLocation = part.serials[selectedSerialIndex].location
		const serialLocationIndex = part.locations.findIndex(location => location.name === serialLocation?.name)
		if (
			serialLocationIndex > -1 &&
			!part.locations[serialLocationIndex].permanent &&
			part.locations[serialLocationIndex].quantity <= 1
		) {
			part.locations = part.locations.toSpliced(serialLocationIndex, 1)
		} else if (serialLocationIndex > -1 && part.locations[serialLocationIndex].quantity > 0) {
			part.locations[serialLocationIndex].quantity = part.locations[serialLocationIndex].quantity - 1
		}

		part.serials[selectedSerialIndex].deleted = true
		selectedSerialIndex = -1
	}

	async function serialLocationChange(
		newSerialLocation: (typeof part.serials)[number]['location'],
		serialIndex: number,
	) {
		const oldSerialLocation = part.serials[serialIndex].location
		part.serials[serialIndex].location = {
			id: newSerialLocation?.id ?? null,
			name: newSerialLocation?.name ?? '',
			description: newSerialLocation?.description ?? '',
			unsaved: true,
		}
		const oldPartLocationIndex = part.locations.findIndex(location => location.name === oldSerialLocation?.name)
		const oldPartLocation = oldPartLocationIndex > -1 ? part.locations[oldPartLocationIndex] : null
		const rank: number | undefined = oldPartLocation?.rank ?? undefined

		if (oldPartLocation && oldPartLocation.quantity > 1) {
			// reduce quantity
			oldPartLocation.quantity = oldPartLocation.quantity - 1
		} else if (oldPartLocation) {
			// remove location
			part.locations = part.locations.toSpliced(oldPartLocationIndex, 1)
		}

		// Tick so changes can propagate to LocationTable before we add new ones
		await tick()

		const newPartLocationIndex = part.locations.findIndex(location => location.name === newSerialLocation?.name)
		const newPartLocation = newPartLocationIndex > -1 ? part.locations[newPartLocationIndex] : null

		const newPartStoreLocation = partStoreLocations.find(location => location.name === newSerialLocation?.name)
		if (newPartLocation) {
			// increase quantity
			newPartLocation.quantity = newPartLocation.quantity + 1
			part.locations = part.locations
		} else if (newPartStoreLocation) {
			// associate "real" location
			locationTable?.addLocation(newPartStoreLocation.name, newPartStoreLocation.id, rank)
		} else {
			// virtual location
			locationTable?.addLocation(newSerialLocation?.name, rank)
		}
		locationTable?.normalizeRanks()
	}

	async function onDefaultVendorChange(vendor: (typeof vendorList)[number] | null) {
		if (!vendor) {
			return
		}

		part.defaultVendor = {
			...vendor,
			coreClassHistory: [],
		}
		// Not awaited because it won't affect anything in this function, just need to call it here when the vendor changes
		updatePricingAndAppraise()
		coreClassHistoryLoading = true
		try {
			const { data } = await vendorCoreClassHistoryQuery.fetch({
				variables: { vendorId: vendor.id },
			})

			part.defaultVendor = {
				...vendor,
				coreClassHistory: data?.vendor.coreClassHistory ?? [],
			}
		} catch (err: any) {
			console.error(err)
			mediator.publish('showMessage', {
				heading: 'Error Loading Core Class History',
				message: err.message,
				color: 'danger',
				time: false,
			})
		} finally {
			coreClassHistoryLoading = false
		}
	}
	//#endregion

	function getModifiedHint(partFieldName: keyof Part): Snippet | undefined {
		if (part[partFieldName] instanceof Date && origPart[partFieldName] instanceof Date) {
			// Here, converting the dates to a certain format for easier comparison
			// And it seems like all the dates here does not include the time, so this should be fine
			return part[partFieldName].toISOString().slice(0, 10) !== origPart[partFieldName].toISOString().slice(0, 10)
				? modifiedHint
				: undefined
		}
		if (!isNaN(Number(part[partFieldName])) && !isNaN(Number(origPart[partFieldName]))) {
			// Here, 0 was the default for most of the currency input value. Setting the value to 0 will return 0.00, which will not be equal to the original value
			// With the Number() constructor, we can make sure that 0.00 is converted to 0
			return Number(part[partFieldName]) !== Number(origPart[partFieldName]) ? modifiedHint : undefined
		}

		return !dequal(part[partFieldName], origPart[partFieldName]) ? modifiedHint : undefined
	}

	// Since we need to check each fields in part.shippingDimension, which is a nested object in part,
	// having a second function that spefically do the checking for that object might be the easiest
	function getModifiedShippingDimensionHint(fieldName: keyof Part['shippingDimensions']): Snippet | undefined {
		// Checking for number value as the "number-type" field are actually saved as a string in the API
		// And since the Input is typed as "number", the returned value from the input will be typed as number
		if (!isNaN(Number(part.shippingDimensions[fieldName])) && !isNaN(Number(origPart.shippingDimensions[fieldName]))) {
			return Number(part.shippingDimensions[fieldName]) !== Number(origPart.shippingDimensions[fieldName])
				? modifiedHint
				: undefined
		}

		return !dequal(part.shippingDimensions[fieldName], origPart.shippingDimensions[fieldName])
			? modifiedHint
			: undefined
	}

	async function doVehicleSearch(search: string) {
		const { data } = await vehicleSearchQuery.fetch({
			variables: {
				filter: {
					search,
				},
			},
		})

		return data?.vehicles.items ?? []
	}

	const vehicleSearchQuery = graphql(`
		query VehicleLookup($filter: VehicleFilter) {
			vehicles(filter: $filter) {
				items {
					bodyStyle
					id
					manufacturer {
						id
						name
					}
					model {
						id
						name
					}
					make
					year
					stockNumber
					store {
						code
						name
					}
					topImage {
						name
						url
					}
					vin
				}
			}
		}
	`)

	const vendorCoreClassHistoryQuery = graphql(`
		query Vendor($vendorId: UInt!) {
			vendor(id: $vendorId) {
				coreClassHistory
			}
		}
	`)
</script>

<Card
	collapsible
	header="Basic Info"
	headerClass="card-header d-flex justify-content-between h4"
	entireHeaderToggles
	bind:bodyShown={$cardsOpen.basic}
>
	<div class="row">
		{#if attachments.length > 0}
			<div class="col-sm-12 col-md-2">
				<ImagePreview
					altTitle={`Tag #${part.tagNumber}`}
					selectedImage={part.topImage}
					images={attachments
						.filter(attachment => attachment.mimeType.startsWith('image/'))
						?.map(attachment => ({ name: attachment.name, path: attachment.path || '' }))}
				/>
			</div>
		{/if}
		<div
			class="col-sm-12 col-md-10"
			class:col-md-10={attachments.length}
			class:col-md-12={!attachments.length}
		>
			<div class="form-row">
				<div class="col-12 col-lg-10">
					<div class="form-row">
						<div class="col-12 col-md-4 col-xl-2">
							<Input
								label="Tag #"
								labelClass={field.tagNumber.global ? 'text-underline' : ''}
								hint={getModifiedHint('tagNumber')}
								bind:value={part.tagNumber}
								placeholder="Leave blank to auto-generate"
								title="Leave blank to auto-generate"
								disabled={(disableGlobalFields && field.tagNumber.global) || !field.tagNumber.editable}
							/>
						</div>
						<div class="col-12 col-md-8 col-xl-4">
							<Autocomplete
								label="Part Type"
								labelClass={field.inventoryType.global ? 'text-underline' : ''}
								title="The type of the inventory record"
								hint={getModifiedHint('inventoryTypeId')}
								required={settingValues.inventory.fields.inventoryType.required}
								disabled={(field.inventoryType.global && disableGlobalFields) || !!part.innodbInventoryid}
								options={inventoryTypeList}
								getLabel={type => (type ? `${type?.inventoryTypeId} - ${type?.name}` : '')}
								bind:value={inventoryTypeData}
								change={option => {
									part.inventoryTypeId = option?.inventoryTypeId ?? null
									part.inventoryType = option
										? {
												id: option.inventoryTypeId,
												name: option.name,
												setId: option.typeSetId,
												// QA List will re-fetch these, we can ignore them here
												typeData1History: [],
												typeData2History: [],
												typeData3History: [],
												typeData4History: [],
										  }
										: null
									updatePricingAndAppraise()
									refetchQa()
								}}
							>
								{#snippet option({ option })}
									<span class="badge bg-primary text-white">{option?.inventoryTypeId}</span>
									{option?.name}
								{/snippet}
							</Autocomplete>
						</div>
						<div class="col-sm-12 col-md-6 col-xl-3">
							<Autocomplete
								label="Assembly Manufacturer"
								labelClass={field.parentManufacturer.global ? 'text-underline' : ''}
								title="The company who manufactures the assembly that this item fits or came off of&#013(Generally the same as the 'Part Manufacturer' for items that are an assembly)"
								hint={getModifiedHint('parentManufacturer')}
								emptyValue={null}
								disabled={field.parentManufacturer.global && disableGlobalFields}
								options={inventoryAssyManufacturers}
								getLabel={manufacturer => manufacturer?.name ?? ''}
								bind:value={part.parentManufacturer}
								change={async option => {
									const manufacturerId = option?.id
									const typeSetId = inventoryTypeData?.typeSetId
									if (manufacturerId && typeSetId) {
										assyModelsLoading = true
										assyModels = await loadModelsForManufacturer(manufacturerId, typeSetId)
										assyModelsLoading = false
									} else {
										assyModels = []
									}
								}}
							></Autocomplete>
						</div>
						<div class="col-sm-12 col-md-6 col-xl-3">
							<Autocomplete
								id="assymodel"
								label="Assembly Model"
								labelClass={field.parentModel.global ? 'text-underline' : ''}
								title="The model name of the assembly that this item fits or came off of&#013(Generally the same as 'Part Model' for items that are an assembly)"
								hint={getModifiedHint('parentModel')}
								isLoading={assyModelsLoading}
								emptyValue={null}
								disabled={field.parentModel.global && disableGlobalFields}
								options={assyModels}
								getLabel={model => (model ? `${model?.name}${model?.active ? '' : ' (inactive)'}` : '')}
								bind:value={part.parentModel}
							></Autocomplete>
						</div>
						<div class="col-sm-12 col-md-6 col-xl-3">
							<Autocomplete
								label="Part Manufacturer"
								labelClass={field.manufacturer.global ? 'text-underline' : ''}
								title="The company who manufactures this item"
								hint={getModifiedHint('manufacturer')}
								emptyValue={null}
								disabled={field.manufacturer.global && disableGlobalFields}
								options={inventoryTrueManufacturers}
								getLabel={manufacturer => manufacturer?.name ?? ''}
								bind:value={part.manufacturer}
								change={async option => {
									const manufacturerId = option?.id
									const typeSetId = inventoryTypeData?.typeSetId
									if (manufacturerId && typeSetId) {
										trueModelsLoading = true
										partModels = await loadModelsForManufacturer(manufacturerId, typeSetId)
										trueModelsLoading = false
									} else {
										partModels = []
									}

									refetchQa()
									updatePricingAndAppraise()
								}}
							></Autocomplete>
						</div>
						<div class="col-sm-12 col-md-6 col-xl-3">
							<Autocomplete
								id="model"
								label="Part Model"
								labelClass={field.model.global ? 'text-underline' : ''}
								title="The name that identifies this item to the manufacturer&#013For aftermarket/interchangeable items this can be the industry standard/OEM model name"
								hint={getModifiedHint('model')}
								isLoading={trueModelsLoading}
								emptyValue={null}
								disabled={field.model.global && disableGlobalFields}
								options={partModels}
								getLabel={model => (model ? `${model?.name}${model?.active ? '' : ' (inactive)'}` : '')}
								bind:value={part.model}
								change={option => {
									const model = option ? partModels.find(model => model.id === option?.id) : null
									if (model && model.defaultShippingDimensions) {
										part.shippingDimensions.weightUnit =
											part.shippingDimensions.weightUnit || model.defaultShippingDimensions.weightUnit
										part.shippingDimensions.measurementUnit =
											part.shippingDimensions.measurementUnit || model.defaultShippingDimensions.measurementUnit
										part.shippingDimensions.weight =
											part.shippingDimensions.weight || model.defaultShippingDimensions.weight
										part.shippingDimensions.length =
											part.shippingDimensions.length || model.defaultShippingDimensions.length
										part.shippingDimensions.width =
											part.shippingDimensions.width || model.defaultShippingDimensions.width
										part.shippingDimensions.height =
											part.shippingDimensions.height || model.defaultShippingDimensions.height
									}
									updatePricingAndAppraise()
									refetchQa()
								}}
							></Autocomplete>
						</div>
						<div class="col-sm-12 col-md-6 col-xl-3">
							<Autocomplete
								label="Category"
								labelClass={field.category.global ? 'text-underline' : ''}
								title="Inventory category can be used to group items by quality and type in advertisement exports"
								hint={getModifiedHint('category')}
								emptyValue={null}
								disabled={shouldDisable('category')}
								required={settingValues.inventory.fields.category.required}
								options={inventoryTypeCategories}
								getLabel={category => category?.name ?? ''}
								bind:value={part.category}
								change={() => {
									updatePricingAndAppraise()
									refetchQa()
								}}
							></Autocomplete>
						</div>
						<div class="col-sm-12 col-md-6 col-lg-3 col-xl-3">
							<Input
								id="oemNum"
								label="OEM #"
								labelClass={field.oemNumber.global ? 'text-underline' : ''}
								type="text"
								title="Optional: how the item is identified by the original manufacturer"
								hint={getModifiedHint('oemNumber')}
								required={settingValues.inventory.fields.oemNumber.required}
								disabled={shouldDisable('oemNumber')}
								bind:value={part.oemNumber}
							/>
						</div>
						<div class="col-sm-12 col-md-6 col-lg-3 col-xl-3">
							<Select
								id="sideSelect"
								label="Side"
								labelClass={field.side.global ? 'text-underline' : ''}
								title="For items that apply to one side of a vehicle or another, what side does this item fit?"
								hint={getModifiedHint('side')}
								class="form-control"
								required={settingValues.inventory.fields.side.required}
								disabled={shouldDisable('side')}
								bind:value={part.side}
							>
								<option value="BOTH">Both</option>
								<option value="LEFT">Left</option>
								<option value="RIGHT">Right</option>
								<option value="NA">N/A</option>
							</Select>
						</div>
						<div class="col-sm-12 col-md-6 col-lg-4 col-xl-3">
							<div class="form-group mb-1">
								<label
									class={field.interchangeNumber.global ? 'col-form-label text-underline' : 'col-form-label'}
									for="interchangeNumber"
								>
									Interchange #
								</label>
								{@render (getModifiedHint('interchangeNumber') ?? getModifiedHint('subInterchangeNumber'))?.()}
								<div class="input-group input-group-sm">
									<input
										id="interchangeNumber"
										title="An interchange number that can group items that share the same application/fitment&#013To explore/create interchange records, use the Interchange screen"
										class="form-control form-control-sm"
										disabled={shouldDisable('interchangeNumber')}
										bind:value={part.interchangeNumber}
									/>
									<div class="input-group-append w-25">
										<input
											id="subInterchangeNumber"
											class="form-control form-control-sm"
											style="border-bottom-left-radius: 0px; border-top-left-radius: 0px;"
											title="Sub-interchange number can be used to sub-categorize items that are partially interchangeable&#013Generally used when items could be made to fit the same application, but with modification"
											disabled={shouldDisable('interchangeNumber')}
											bind:value={part.subInterchangeNumber}
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="col-4 col-xl-2">
							<Input
								label="Quantity"
								class="text-right"
								type="number"
								inputmode="decimal"
								title="Total quantity on hand of the item at this store"
								hint={getModifiedHint('quantity')}
								disabled={shouldDisable('quantity') || !part.deplete}
								bind:value={part.quantity}
							/>
						</div>
						<div class="col-4 col-xl-2">
							<Input
								readonly
								label="On Hold"
								class={part.deplete ? 'text-right' : ''}
								type={part.deplete ? 'number' : 'text'}
								inputmode="decimal"
								title="The total quantity of the item on hold at this store (on any documents)"
								tabindex={-1}
								value={part.deplete ? part.quantityOnHold : 'N/A (not depletable)'}
							/>
						</div>
						<div class="col-4 col-xl-2">
							<Input
								readonly
								label="Available"
								class={part.deplete ? 'text-right' : ''}
								type={part.deplete ? 'number' : 'text'}
								inputmode="decimal"
								title="Quantity available for sale at this store. This is the total quantity on hand minus any quantity held."
								tabindex={-1}
								value={part.deplete ? part.quantityAvailable : 'N/A (not depletable)'}
							/>
						</div>
					</div>
				</div>
				<div class="col-12 col-lg-2">
					<TextArea
						placeholder="Internal notes won't appear on any customer-facing reports or online."
						label="Internal Notes"
						labelClass={field.internalNotes.global ? 'text-underline' : ''}
						labelParentClass="h-100 d-flex flex-column"
						class="flex-grow-1"
						title="Internal notes for this item&#013This information is not shown to the customer or in marketing"
						hint={getModifiedHint('notes')}
						disabled={shouldDisable('internalNotes')}
						bind:value={part.notes}
					/>
				</div>
			</div>
		</div>
		<div class="col-12">
			<div
				class="card mt-3"
				class:d-none={!typeFields.length && (part.serialized || !part.inventoryOptions.length)}
			>
				<h5 class="card-header">Custom Fields</h5>
				<div class="card-body">
					<!-- "Top" QA List -->
					<QaList
						{shouldDisable}
						{qaInputChange}
						{optionValueMap}
						{inventoryTypeData}
						serials={part.serials}
						category={part.category}
						options={part.serialized ? [] : selectedSerialQuestions}
						typeFieldListChange={updatedTypeFieldList => (typeFields = updatedTypeFieldList)}
						optionListChange={optionList => {
							// this new array has null for all the values, but we track the values in the map so that's fine
							part.inventoryOptions = optionList
						}}
						typeFieldChange={({ label, value }) => {
							if (part.typeField1?.label === label) {
								part.typeField1.data = value
							} else if (part.typeField2?.label === label) {
								part.typeField2.data = value
							} else if (part.typeField3?.label === label) {
								part.typeField3.data = value
							} else if (part.typeField4?.label === label) {
								part.typeField4.data = value
							}
						}}
						bind:typeFields
						bind:this={qaList}
					/>
				</div>
			</div>
		</div>
	</div>
</Card>

<Card
	collapsible
	headerClass="card-header d-flex justify-content-between h4"
	class="mt-2"
	entireHeaderToggles
	bind:bodyShown={$cardsOpen.marketing}
>
	{#snippet header()}
		<div>
			Marketing
			<HtpInfoButton
				sku={part.inventoryId}
				storeId={part.storeId}
			></HtpInfoButton>
		</div>
	{/snippet}
	<PartMarketingFields
		{field}
		{origPart}
		{saleClasses}
		{shouldDisable}
		{getModifiedHint}
		{inventoryConditions}
		{inventoryTypeCategories}
		vehicle={part.vehicle}
		categoryChange={refetchQa}
		modelName={part.model?.name ?? null}
		manufacturerName={part.manufacturer?.name ?? null}
		bind:part
		bind:this={marketingFields}
	></PartMarketingFields>
</Card>

<Card
	collapsible
	header="Stocking"
	headerClass="card-header d-flex justify-content-between h4"
	class="mt-2"
	entireHeaderToggles
	bind:bodyShown={$cardsOpen.stocking}
>
	<div class="form-row">
		<div class="col-12 col-md-4 col-xl-6">
			<div class="form-row">
				<div class="col-12 col-xl-4">
					<Select
						label="GL Category"
						labelClass="pt-0 text-underline"
						title="The general ledger category of an item represents its accounting category. This controls which accounts&#013will be hit when this item is used on various documents."
						hint={getModifiedHint('glCategory')}
						emptyValue={null}
						required={settingValues.inventory.fields.glCategory.required}
						disabled={!hasPermission('PARTS_CAN_EDIT_GLCATEGORY', part.storeId)}
						bind:value={part.glCategory}
					>
						{#each glCategories as gl}
							<option value={gl}>{gl.name}</option>
						{/each}
					</Select>
				</div>
				<div class="col-12 col-xl-4">
					<Select
						id="statusSelect"
						label="Status"
						labelClass="pt-0"
						title="Part status controls whether the item shows up in searches and reports:&#013A: item is on hand and available for sale&#013D: item is deleted, generally for mistake items&#013C: the item is removed from stock because it has been crushed/scrapped/destroyed&#013H: the item is currently placed on manual hold (different from quantity on hold from open documents)&#013S: stock has been depleted (sold) and will not be replenished&#013T: stock has been transferred to another store"
						hint={getModifiedHint('status')}
						disabled={shouldDisable('status')}
						bind:value={part.status}
					>
						<option value="A">A - Available</option>
						<option value="H">H - On Hold</option>
						<option value="S">S - Sold</option>
						<option value="C">C - Crushed</option>
						<option value="D">D - Deleted</option>
						<option value="T">T - Transferred</option>
						<option value="B">B - Bidding</option>
					</Select>
				</div>
				<div class="col-12 col-xl-4">
					<Select
						id="userStatus"
						label="User Status"
						labelClass={`pt-0 ${field.userStatus.global ? 'text-underline' : ''}`}
						title="User statuses are controlled by the company and represent custom internal processes"
						hint={getModifiedHint('userStatus')}
						disabled={field.userStatus.global && disableGlobalFields}
						required={settingValues.inventory.fields.userStatus.required}
						emptyValue={part.userStatus === '' ? '' : null}
						bind:value={part.userStatus}
					>
						{#each userPartStatusList as { status, description }}
							<option value={status}>{status} - {description}</option>
						{/each}
					</Select>
				</div>
			</div>
			<Fieldset label="Stock Configuration">
				<div class="form-row">
					<div class="col-12 col-md-6">
						<Checkbox
							label="Deplete"
							labelClass={field.deplete.global ? 'text-underline' : ''}
							title="If a part is not depletable, it means that the system should not track its stock levels or cost. &#013This is commonly used for items that represent fees, charges, or shop supplies that are (essentially) unlimited"
							hint={getModifiedHint('deplete')}
							disabled={shouldDisable('deplete')}
							bind:checked={part.deplete}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label="Single Quantity"
							labelClass={field.singleQuantity.global ? 'text-underline' : ''}
							title="Single quantity parts don't allow quantity to go above 1 or multiple stocking locations &#013This is useful for simple parts and can simplify warehouse count operations"
							hint={getModifiedHint('singleQuantity')}
							disabled={shouldDisable('singleQuantity')}
							bind:checked={part.singleQuantity}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label="Show Online"
							labelClass={field.public.global ? 'text-underline' : ''}
							title="If checked, any configured marketing exports are allowed to view this item and advertise it"
							hint={getModifiedHint('public')}
							disabled={shouldDisable('public')}
							bind:checked={part.public}
							onchange={() => {
								if (!part.public) {
									part.saleClass = noneSaleClass
								}
							}}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label="Taxable"
							labelClass={field.taxable.global ? 'text-underline' : ''}
							title="Whether this item will be marked as taxable on customer documents by default"
							hint={getModifiedHint('taxable')}
							disabled={shouldDisable('taxable')}
							bind:checked={part.taxable}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label="Use Serial #s"
							labelClass={field.serialized.global ? 'text-underline' : ''}
							title="Whether this item uses serial numbers, meaning every individual item on hand will be identified by a unique serial # with its own Q&A. Once enabled and saved, this cannot be disabled."
							disabled={origPart.serialized || shouldDisable('serialized')}
							bind:checked={part.serialized}
							onchange={() => serializePart(part.serialized, '')}
						>
							{#snippet hint()}
								{#if inventoryTypeData?.requireSerialization && !part.serialized}
									<small class="ml-1 text-danger">Required</small>
								{/if}
								{@render getModifiedHint('serialized')?.()}
							{/snippet}
						</Checkbox>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							id="replenishable"
							label="Replenishable"
							labelClass={field.replenish.global ? 'text-underline' : ''}
							title="Replenishable parts have several qualities: &#013 1. The item's status will stay 'A' when quantity on hand reaches 0 (instead of going to 'S'). &#013 2. The part allows editing of the replenishable information card, where additional fields can be filled out that relate to new/aftermarket parts."
							hint={getModifiedHint('replenishable')}
							disabled={shouldDisable('replenish')}
							bind:checked={part.replenishable}
							onchange={replenishableChange}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label="Tag Printed"
							labelClass={field.printTag.global ? 'text-underline' : ''}
							title="Whether a tag has been printed for this inventory record yet"
							hint={getModifiedHint('tagPrinted')}
							disabled={shouldDisable('printTag')}
							bind:checked={part.tagPrinted}
						/>
					</div>
				</div>
			</Fieldset>
		</div>
		<div class="col-12 col-md-8 col-xl-6">
			<LocationTable
				{partStoreLocations}
				{allowVirtualLocations}
				storeId={part.storeId}
				partQuantity={part.quantity}
				serialized={part.serialized}
				varianceLocationName={settingValues.inventory.varianceLocationName ?? 'Variance'}
				bind:locations={part.locations}
				bind:this={locationTable}
			></LocationTable>
		</div>
	</div>
	{#if part.serialized}
		<div class="form-row mt-2">
			<div class="col-12 col-xl-6 mb-3 mb-xl-0">
				<div class="card h-100">
					<div class="card-header">
						<h6 class="d-inline">Serials</h6>
					</div>
					<div class="card-body">
						<Table
							filterColumnClass="col-12 col-sm-6 col-lg-3 col-xl-6 align-self-end"
							rows={part.serials}
							columns={serialTableColumns}
							filterEnabled
							responsive
						>
							{#snippet body({ rows })}
								{#if !part.singleQuantity}
									<tr
										class="cursor-pointer"
										class:table-primary={selectedSerialIndex === -1}
										onclick={() => (selectedSerialIndex = -1)}
									>
										<td></td>
										<td>Default Serial Q&amp;A</td>
										<td>-</td>
										<td>-</td>
										<td>-</td>
										<td>-</td>
									</tr>
								{/if}
								{#each rows as serial (serial.uuid)}
									{#if (serial.originalIndex || serial.originalIndex === 0) && !(serial.deleted && serial.id === null)}
										<tr
											class="cursor-pointer"
											class:table-primary={selectedSerialIndex === serial.originalIndex}
											class:table-danger={serial.deleted}
											onclick={() => (selectedSerialIndex = serial.originalIndex)}
										>
											<td class="text-center">
												{#if part.serials[serial.originalIndex].deleted}
													<Icon
														icon="trash"
														class="text-danger"
													/>
												{:else if part.serials[serial.originalIndex].id === null || !dequal(part.serials[serial.originalIndex], origPart.serials[serial.originalIndex])}
													<Icon
														icon="pencil"
														class="text-warning"
													/>
												{/if}
											</td>
											<td>
												<Input
													required
													id="serial-number-{serial.originalIndex}"
													placeholder="Enter a Serial Number"
													showLabel={false}
													labelParentClass="form-group m-0"
													disabled={disableGlobalFields || part.status !== 'A' || serial.deleted}
													value={serial.number}
													onchange={event => (part.serials[serial.originalIndex].number = getEventValue(event) ?? '')}
												/>
											</td>
											<td>
												<span
													class="badge w-100"
													class:badge-primary={serial.status === 'AVAILABLE'}
													class:badge-secondary={serial.status === 'ON_HOLD' || serial.status === 'IN_TRANSIT'}
													class:badge-danger={!['AVAILABLE', 'ON_HOLD', 'IN_TRANSIT'].includes(serial.status)}
													>{serial.displayStatus}</span
												>
											</td>
											<td>
												<Autocomplete
													required
													id="serial-location-{serial.originalIndex}"
													placeholder="Select a Location"
													showLabel={false}
													canAddNew={allowVirtualLocations}
													disabled={part.status !== 'A' || disableGlobalFields || serial.deleted}
													labelParentClass="form-group m-0"
													options={partStoreLocations}
													getLabel={location => location?.name ?? ''}
													createOption={name => ({
														id: null,
														name,
														description: '',
													})}
													value={serial.location}
													change={option => serialLocationChange(option, serial.originalIndex)}
												/>
											</td>
											<td style:white-space="nowrap">{serial.source}</td>
											<td style:white-space="nowrap">{serial.usedOn}</td>
										</tr>
									{/if}
								{/each}
							{/snippet}
						</Table>
					</div>
					<div class="card-footer">
						<Button
							outline
							size="sm"
							color="success"
							iconClass="plus"
							disabled={disableGlobalFields || part.status !== 'A' || (part.singleQuantity && part.serials.length > 0)}
							onclick={() => addSerial()}>New Serial</Button
						>

						<Button
							outline
							size="sm"
							color="danger"
							disabled={part.status !== 'A' || selectedSerialIndex === -1 || disableGlobalFields}
							iconClass="trash"
							onclick={() => deleteSerial()}>Delete Serial</Button
						>
					</div>
				</div>
			</div>
			<div class="col-12 col-xl-6">
				<div class="card h-100">
					<div class="card-header">
						<h6 class="d-inline mr-2">
							{(part.serials[selectedSerialIndex] || {}).number || 'Default'} Custom Fields
						</h6>
						<small class:d-none={selectedSerialIndex !== -1}>(Will affect newly created serials)</small>
					</div>
					<div class="card-body">
						<QaList
							{shouldDisable}
							{qaInputChange}
							{optionValueMap}
							{selectedSerialId}
							{inventoryTypeData}
							{selectedSerialUuid}
							typeFields={[]}
							serials={part.serials}
							category={part.category}
							options={selectedSerialQuestions}
							optionListChange={optionList => {
								// this new array has null for all the values, but we track the values in the map so that's fine
								part.inventoryOptions = optionList
							}}
							bind:this={serialQaList}
						/>
					</div>
				</div>
			</div>
		</div>
	{/if}
	{#if part.replenishable}
		<Card
			collapsible
			header="Aftermarket & Replenishable Info"
			headerClass="card-header d-flex justify-content-between h5"
			class="mt-2"
			entireHeaderToggles
			bind:bodyShown={$cardsOpen.replenishable}
		>
			<div class="card-group">
				<div class="card">
					<h6 class="card-header">Vendor Info</h6>
					<div class="card-body">
						<div class="form-row align-items-end">
							<div class="col-12">
								<div class="form-group mb-1">
									<Autocomplete
										label="Default Vendor"
										labelClass={field.defaultVendor.global ? 'text-underline' : ''}
										emptyValue={null}
										title="The default vendor this item is purchased from. This choice affects how the item is ordered on the inventory overview screen and historical ordering information."
										hint={getModifiedHint('defaultVendor')}
										disabled={shouldDisable('defaultVendor')}
										options={vendorList}
										getLabel={vendor => {
											const vendorName = vendor?.contactName ?? vendor?.companyName ?? ''
											return vendor?.code ? `${vendor.code}${vendorName ? ` - ${vendorName}` : ''}` : vendorName
										}}
										value={part.defaultVendor}
										change={onDefaultVendorChange}
									></Autocomplete>
								</div>
							</div>
							<div class="col-12">
								<Input
									label="Part Number"
									labelClass={field.partNumber.global ? 'text-underline' : ''}
									title="The part number of the item. This is what the item is referred to as by the default purchase vendor"
									hint={getModifiedHint('partNumber')}
									disabled={shouldDisable('partNumber')}
									bind:value={part.partNumber}
									onchange={updatePricingAndAppraise}
								/>
							</div>
							<div class="col-12">
								<Input
									label="Vendor Product Code"
									labelClass={field.vendorProductCode.global ? 'text-underline' : ''}
									title="A vendor product code"
									hint={getModifiedHint('vendorProductCode')}
									disabled={shouldDisable('vendorProductCode')}
									bind:value={part.vendorProductCode}
								/>
							</div>
							<div class="col-12">
								<Input
									label="Vendor Popularity Code"
									labelClass={field.vendorPopularityCode.global ? 'text-underline' : ''}
									title="The popularity code of this item from the default purchase vendor"
									hint={getModifiedHint('vendorPopularityCode')}
									disabled={shouldDisable('vendorPopularityCode')}
									bind:value={part.vendorPopularityCode}
								/>
							</div>
							<div class="col-12">
								<Select
									label="Core Class"
									labelClass={field.coreClass.global ? 'text-underline' : ''}
									title="Items that share a core class are interchangeable when returned to a vendor for a core exchange."
									hint={getModifiedHint('coreClass')}
									disabled={shouldDisable('coreClass')}
									isLoading={coreClassHistoryLoading}
									bind:value={part.coreClass}
								>
									{#each part.defaultVendor?.coreClassHistory ?? [] as coreClass}
										<option value={coreClass}>{coreClass}</option>
									{/each}
								</Select>
							</div>
							<div class="col-12">
								<Select
									label="Sell Price Class"
									labelClass={field.sellPriceClass.global ? 'text-underline' : ''}
									title="The sell price class of an item allows categorizing similar items for easy discount pricing to certain customers.&#013If an item is assigned to a class that is more specific than a customer's discount level, it will be applied.&#013For example: an item assigned to class 'Engines - Used' will trigger a customer's discount on the class 'Engines'"
									emptyValue={null}
									hint={getModifiedHint('sellPriceClassId')}
									disabled={shouldDisable('sellPriceClass')}
									options={sellPriceClasses}
									bind:value={part.sellPriceClassId}
								>
									{#snippet option({ option })}
										<option value={option.id}>
											{#if option.parent}{option.parent.name} -
											{/if}{option.name}
										</option>
									{/snippet}
								</Select>
							</div>
						</div>
					</div>
				</div>
				<div class="card flex-md-grow-2 flex-xl-grow-3">
					<h6 class="card-header">Ordering Info</h6>
					<div class="card-body">
						<div class="form-row align-items-end">
							<div class="col-12 col-md-3">
								<Select
									label="Stock Type"
									labelClass={field.stockType.global ? 'text-underline' : ''}
									title="This controls how and if this item is stocked regularly"
									hint={getModifiedHint('stockType')}
									disabled={shouldDisable('stockType')}
									bind:value={part.stockType}
								>
									<option value="SPECIAL_ORDER">Special Order</option>
									<option value="STOCKED">Stocked</option>
									<option value="OBSOLETE">Obsolete</option>
								</Select>
							</div>
							<div class="col-12 col-md-3">
								<Select
									label="Stock Method"
									labelClass={field.stockMethod.global ? 'text-underline' : ''}
									title="If this item is regularly stocked, what method is used to calculate order quantity"
									hint={getModifiedHint('stockMethod')}
									disabled={shouldDisable('stockMethod')}
									bind:value={part.stockMethod}
								>
									<option value="NONE">None</option>
									<option value="MIN_MAX">Min/Max</option>
									<option value="HISTORY">History</option>
								</Select>
							</div>
							<div class="col-12 col-md-3">
								<Input
									label="Stocking Days"
									labelClass={field.stockingDays.global ? 'text-underline' : ''}
									title="On average, how many days it will take to restock the item from the default purchase vendor"
									hint={getModifiedHint('stockingDays')}
									disabled={shouldDisable('stockingDays')}
									bind:value={part.stockingDays}
									type="number"
								/>
							</div>
							<div class="col-12 col-md-3">
								<Input
									label="Safety Stock"
									labelClass={field.safetyStockPercent.global ? 'text-underline' : ''}
									title="An additional quantity to keep in stock, above and beyond the quantity to cover the demand during the lead time"
									hint={getModifiedHint('safetyStockPercent')}
									disabled={shouldDisable('safetyStockPercent')}
									type="number"
									inputmode="decimal"
									bind:value={part.safetyStockPercent}
								>
									{#snippet append()}
										<span class="input-group-text">%</span>
									{/snippet}
								</Input>
							</div>
							<div class="col-12 col-md-6">
								<div class="form-row">
									<div class="col-12">
										<Checkbox
											label="Use Vendor Order Multiplier"
											labelClass={field.purchaseFactor.global ? 'text-underline' : ''}
											title="When checked, show each 'pack' of the buy package quantity as 1 order quantity on vendor-facing documents"
											hint={getModifiedHint('useVendorOrderMultiplier')}
											disabled={shouldDisable('purchaseFactor')}
											bind:checked={part.useVendorOrderMultiplier}
										/>
									</div>
									<div class="col-12 col-md-6">
										<Input
											label="Buy Package"
											labelClass={field.buyPackage.global ? 'text-underline' : ''}
											type="number"
											inputmode="decimal"
											title="The quantity that this item is normally bought in groups of (defaults to 1)"
											hint={getModifiedHint('buyPackage')}
											disabled={shouldDisable('buyPackage')}
											bind:value={part.buyPackage}
										/>
									</div>
									<div class="col-12 col-md-6">
										<Input
											label="Sell Package"
											labelClass={field.sellPackage.global ? 'text-underline' : ''}
											type="number"
											inputmode="decimal"
											title="The quantity that this item is normally sould in groups of (defaults to 1)"
											hint={getModifiedHint('sellPackage')}
											disabled={shouldDisable('sellPackage')}
											bind:value={part.sellPackage}
										/>
									</div>
								</div>
							</div>
							<div class="col-6 col-md-3">
								<Input
									label="Minimum Quantity"
									labelClass={field.minQuantity.global ? 'text-underline' : ''}
									type="number"
									inputmode="decimal"
									title="The quantity at which to perform a stock order."
									hint={getModifiedHint('minQuantity')}
									disabled={shouldDisable('minQuantity')}
									bind:value={part.minQuantity}
								/>
							</div>
							<div class="col-6 col-md-3">
								<Input
									label="Maximum Quantity"
									labelClass={field.maxQuantity.global ? 'text-underline' : ''}
									title="The quantity to order back up to when performing stock ordering.This is also known as the stocking point."
									type="number"
									inputmode="decimal"
									hint={getModifiedHint('maxQuantity')}
									disabled={shouldDisable('maxQuantity')}
									bind:value={part.maxQuantity}
								/>
							</div>
							<div class="col-12 col-md-3">
								<Input
									label="Popularity Code"
									labelClass={field.popularityCode.global ? 'text-underline' : ''}
									title="The popularity code of this item at this store. Popularity code can be used to categorize how quickly this item moves and its stock priority"
									hint={getModifiedHint('popularityCode')}
									disabled={shouldDisable('popularityCode')}
									bind:value={part.popularityCode}
								/>
							</div>
							<div class="col-12 col-md-6">
								<Checkbox
									label="Seasonal"
									labelClass={field.seasonal.global ? 'text-underline' : ''}
									title="Check this if the item follows a distinctly seasonal demand pattern"
									hint={getModifiedHint('seasonal')}
									disabled={shouldDisable('seasonal')}
									bind:checked={part.seasonal}
								/>
							</div>
						</div>
					</div>
					<div class="card-header border-top d-flex justify-content-between align-items-center flex-wrap">
						<h6 class="mb-0">Calculated Values</h6>
						<Button
							outline
							size="sm"
							disabled={!hasPermission('PARTS_CAN_EDIT_INFO', part.storeId)}
							icon={{
								class: 'fa-flip-horizontal',
								icon: 'arrow-turn-up',
							}}
							onclick={useSuggestedQuantities}>Use Suggested Quantities</Button
						>
					</div>
					<div class="card-body">
						<div class="form-row align-items-end">
							<div class="col-6 col-md-3">
								<Input
									readonly
									label="Suggested Minimum Quantity"
									type="number"
									inputmode="decimal"
									title="A suggested minimum quantity value calculated based on historical demand."
									disabled={disableGlobalFields && field.minQuantity.global}
									bind:value={part.suggestedMinQuantity}
								/>
							</div>
							<div class="col-6 col-md-3">
								<Input
									readonly
									label="Suggested Maximum Quantity"
									type="number"
									inputmode="decimal"
									title="A suggested maximum quantity value calculated based on historical demand."
									disabled={disableGlobalFields && field.maxQuantity.global}
									bind:value={part.suggestedMaxQuantity}
								/>
							</div>
							<div class="col-6 col-md-3">
								<Input
									readonly
									label="Avg Demand Per Day"
									title="Historical average daily demand over the last year. This is used to calculate suggested stocking levels."
									bind:value={part.averageDemandPerDay}
								/>
							</div>
							<div class="col-6 col-md-3">
								<Input
									readonly
									label="Avg Demand Per Month"
									title="Historical average monthly demand over the last year. This is used to calculate suggested stocking levels."
									bind:value={part.averageDemandPerMonth}
								/>
							</div>
							<div class="col-12 col-md-4">
								<Input
									readonly
									label="Lead Time (days)"
									title="This is the average historical lead time for this item from the default purchase vendor.  Lead time represents&#013how many days it generally takes to fulfill orders of this item after it has been ordered.&#013Note: only purchase orders started in last 90 days are considered"
									bind:value={part.vendorLeadTime}
								/>
							</div>
							<div class="col-12 col-md-4">
								<Input
									readonly
									label="Suggested Safety Stock"
									type="number"
									inputmode="decimal"
									title="A suggested safety stock value calculated based on historical demand."
									bind:value={part.suggestedSafetyStockPercent}
								>
									{#snippet append()}
										<span class="input-group-text">%</span>
									{/snippet}
								</Input>
							</div>
							<div class="col-12 col-md-4">
								<Input
									label="Freeze Calculations Until"
									labelClass={field.freezeDate.global ? 'text-underline' : ''}
									title="If checked, this represents a timer for when the item can first start to calculate historical demand patterns.&#013Before this date, the system will not attempt to automatically calculate any stocking statistics."
									type="date"
									hint={getModifiedHint('freezeUntil')}
									disabled={shouldDisable('freezeDate')}
									value={part.freezeUntil ? part.freezeUntil.toISOString().slice(0, 10) : null}
									onchange={event => (part.freezeUntil = new Date(getEventValue(event)))}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Card>
	{/if}
	<Card
		collapsible
		headerClass="card-header flex-wrap"
		class="mt-2"
		entireHeaderToggles
		bind:bodyShown={$cardsOpen.vehicle}
	>
		{#snippet header()}
			<div class="d-flex flex-fill justify-content-between">
				<h5>Vehicle</h5>
				<div>
					<Button
						outline
						size="sm"
						class="mr-1"
						iconClass="search"
						title="Search for a vehicle record to associate with this part."
						disabled={disableGlobalFields}
						onclick={event => {
							event.stopPropagation()
							vehicleLookupModal?.show()
						}}
					>
						Find <span class="d-none d-sm-inline">Vehicle</span>
					</Button>
					<Button
						outline
						size="sm"
						class="mr-4"
						iconClass="truck"
						title="Open the vehicle record associated with this inventory record."
						disabled={!part.vehicleId}
						href={part.vehicleId
							? asr.makePath('app.vehicle', { vehicleId: part.vehicleId, storeId: part.storeId }, {})
							: null}
						onclick={event => event.stopPropagation()}
						>Open <span class="d-none d-sm-inline">Vehicle</span>
					</Button>
				</div>
			</div>
		{/snippet}

		{#if part.vehicle && part.vehicleId}
			<div class="form-row">
				{#if part.vehicle.topImage}
					<div class="col-md-auto mb-3">
						<a
							target="_blank"
							href={part.vehicle.topImage.url}
						>
							<img
								class="img-fluid rounded"
								src="{part.vehicle.topImage.url}?height=150&width=150&fit=contain"
								title={part.vehicle.topImage.name}
								alt={part.vehicle.topImage.name}
							/>
						</a>
					</div>
				{/if}
				<div class="col-md">
					<Input
						readonly
						id="stockNumber"
						label="Stock Number"
						title="Vehicle record associated with this inventory record (if any). If a vehicle is associated with a part record, cost, revenue, and profit will be tracked back to that vehicle unit."
						value={part.vehicle.stockNumber}
					/>
				</div>
				<div class="col-md-6 col-xl-4">
					<Input
						readonly
						label="Year"
						value={part.vehicle.year}
					/>
				</div>
				<div class="col-md-6 col-xl-4">
					<Input
						readonly
						label="Make"
						title="Vehicle make that this item fits or came off of"
						value={part.vehicle.manufacturer?.name ?? ''}
					/>
				</div>
				<div class="col-md-6 col-xl-4">
					<Input
						readonly
						label="Model"
						title="Vehicle model that this item fits or came off of"
						value={part.vehicle.model?.name ?? ''}
					/>
				</div>
				<div class="col-md">
					<Input
						readonly
						label="VIN"
						title="Vehicle Identification Number"
						value={part.vehicle.vin}
					/>
				</div>
				<div class="col-md">
					<Input
						readonly
						label="Body"
						title="Body style of the vehicle this item came off of"
						value={part.vehicle.bodyStyle}
					/>
				</div>
			</div>
		{:else}
			<div class="form-row">
				<div class="col-md-4 col-xl-2">
					<Input
						id="vehicleYear"
						label="Year"
						hint={getModifiedHint('vehicleYear')}
						labelClass={field.year.global ? 'text-underline' : ''}
						disabled={disableGlobalFields && field.year.global}
						bind:value={part.vehicleYear}
						onchange={updatePricingAndAppraise}
					/>
				</div>
				<div class="col-md-4 col-xl-3">
					<Input
						label="Make"
						hint={getModifiedHint('vehicleMake')}
						labelClass={field.vehicleMake.global ? 'text-underline' : ''}
						list="vehicleMakes"
						style="text-transform:uppercase"
						title="Vehicle make that this item fits or came off of"
						disabled={disableGlobalFields && field.vehicleMake.global}
						bind:value={part.vehicleMake}
						onchange={async () => {
							if (part.vehicleMake) {
								vehicleModels = await loadVehicleModelsForMake(part.vehicleMake)
							} else {
								vehicleModels = []
							}
							updatePricingAndAppraise()
						}}
					/>
					<datalist id="vehicleMakes">
						{#each vehicleMakes as make}
							<option value={make}>{make}</option>
						{/each}
					</datalist>
				</div>
				<div class="col-md-4 col-xl-3">
					<Input
						label="Model"
						hint={getModifiedHint('vehicleModel')}
						labelClass={field.vehicleModel.global ? 'text-underline' : ''}
						list="vehicleModels"
						style="text-transform:uppercase"
						title="Vehicle model that this item fits or came off of"
						disabled={disableGlobalFields && field.vehicleModel.global}
						bind:value={part.vehicleModel}
						onchange={() => updatePricingAndAppraise()}
					/>
					<datalist id="vehicleModels">
						{#each vehicleModels as model}
							<option value={model.toUpperCase()}>{model}</option>
						{/each}
					</datalist>
				</div>
				<div class="col-md-4 col-xl-2">
					<Input
						id="vehicleVin"
						label="VIN"
						labelClass="text-underline"
						title="Vehicle Identification Number"
						hint={getModifiedHint('vehicleVin')}
						disabled={disableGlobalFields && field.vin.global}
						bind:value={part.vehicleVin}
					/>
				</div>
				<div class="col-md-4 col-xl-2">
					<Input
						id="bodyStyle"
						label="Body Style"
						hint={getModifiedHint('bodyStyle')}
						labelClass={field.bodyStyle.global ? 'text-underline' : ''}
						title="Body style of the vehicle this item came off of"
						disabled={disableGlobalFields && field.bodyStyle.global}
						bind:value={part.bodyStyle}
					/>
				</div>
			</div>
		{/if}
	</Card>
</Card>

<div class="form-row p-0">
	<div
		class="col-12 col-xl-6"
		class:d-flex={$cardsOpen.fulfillment}
	>
		<Card
			collapsible
			header="Fulfillment"
			headerClass="card-header d-flex justify-content-between h4"
			class="mt-2 {$cardsOpen.fulfillment ? 'flex-grow-1' : ''}"
			entireHeaderToggles
			bind:bodyShown={$cardsOpen.fulfillment}
		>
			<div class="table-responsive">
				<table class="table table-sm table-bordered mb-0">
					<thead>
						<tr>
							<th style="width: 80%;"></th>
							<th
								style="min-width: 45px;"
								class="text-right text-nowrap">Days to Return</th
							>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td class="align-bottom">
								<Checkbox
									label="Returnable"
									labelClass={field.returnable.global ? 'text-underline' : ''}
									hint={!origPart.returnable && !part.returnable
										? undefined
										: getModifiedHint('returnable') || getModifiedHint('daysToReturn')}
									title="If checked, this item can be returned by customers"
									disabled={shouldDisable('returnable')}
									bind:checked={part.returnable}
									onchange={() => {
										if (!part.returnable) {
											part.daysToReturn = 0
										}
									}}
								/>
							</td>
							<td>
								<Input
									class="text-right"
									labelParentClass="mb-0"
									showLabel={false}
									title="The maximum number of days until a return will no longer be accepted from a customer"
									disabled={!part.returnable || (disableGlobalFields && field.returnable.global)}
									value={part.returnable ? part.daysToReturn : null}
									onchange={event => numberChange(event, 'daysToReturn')}
								/>
							</td>
						</tr>
						<tr>
							<td class="align-bottom">
								<Checkbox
									label="Core Required"
									labelClass={field.coreRequired.global ? 'text-underline' : ''}
									hint={!origPart.coreRequired && !part.coreRequired
										? undefined
										: getModifiedHint('coreRequired') || getModifiedHint('daysToReturnCore')}
									title="If checked, when this item is sold to a customer, a core exchange will be expected&#013This means an inherent core charge will be added to documents and it allow a dirty core exchange to refund that core charge"
									disabled={shouldDisable('coreRequired')}
									bind:checked={part.coreRequired}
									onchange={() => {
										if (!part.coreRequired) {
											part.daysToReturnCore = 0
										}
									}}
								/>
							</td>
							<td>
								<Input
									class="text-right"
									labelParentClass="mb-0"
									showLabel={false}
									title="The maximum number of days until a core exchange will no longer be accepted from a customer"
									disabled={!part.coreRequired || (disableGlobalFields && field.coreRequired.global)}
									value={part.coreRequired ? part.daysToReturnCore : null}
									onchange={event => numberChange(event, 'daysToReturnCore')}
								/>
							</td>
						</tr>
						<tr>
							<td class="align-bottom">
								<Checkbox
									label="Returnable to Vendor"
									labelClass={field.returnableToVendor.global ? 'text-underline' : ''}
									hint={!origPart.returnableToVendor && !part.returnableToVendor
										? undefined
										: getModifiedHint('returnableToVendor') || getModifiedHint('daysToReturnToVendor')}
									title="If checked, this item can be returned to the vendor on a return purchase order"
									disabled={shouldDisable('returnableToVendor')}
									bind:checked={part.returnableToVendor}
									onchange={() => {
										if (!part.returnableToVendor) {
											part.daysToReturnToVendor = 0
										}
									}}
								/>
							</td>
							<td>
								<Input
									class="text-right"
									labelParentClass="mb-0"
									showLabel={false}
									title="The maximum number of days until a return will no longer be accepted by the vendor"
									disabled={!part.returnableToVendor || (disableGlobalFields && field.returnableToVendor.global)}
									value={part.returnableToVendor ? part.daysToReturnToVendor : null}
									onchange={event => numberChange(event, 'daysToReturnToVendor')}
								/>
							</td>
						</tr>
						<tr>
							<td class="align-bottom">
								<Checkbox
									label="Vendor Core Required"
									labelClass={field.coreRequiredToVendor.global ? 'text-underline' : ''}
									hint={!origPart.coreRequiredToVendor && !part.coreRequiredToVendor
										? undefined
										: getModifiedHint('coreRequiredToVendor') || getModifiedHint('daysToReturnCoreToVendor')}
									title="If checked, when this item is purchased from a vendor, a core exchange will be expected&#013This means an inherent core charge will be added to POs in the the amount of the core cost, and a core must be exchanged to refund that core charge"
									disabled={shouldDisable('coreRequiredToVendor')}
									bind:checked={part.coreRequiredToVendor}
									onchange={() => {
										if (!part.coreRequiredToVendor) {
											part.daysToReturnCoreToVendor = 0
										}
									}}
								/>
							</td>
							<td>
								<Input
									class="text-right"
									labelParentClass="mb-0"
									showLabel={false}
									title="The maximum number of days until a core exchange will no longer be accepted by the vendor"
									disabled={!part.coreRequiredToVendor || (disableGlobalFields && field.coreRequiredToVendor.global)}
									value={part.coreRequiredToVendor ? part.daysToReturnCoreToVendor : null}
									onchange={event => numberChange(event, 'daysToReturnCoreToVendor')}
								/>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</Card>
	</div>
	<div class="col-12 col-xl-6">
		<Card
			collapsible
			header="Sales and Shipping"
			headerClass="card-header d-flex justify-content-between h4"
			class="mt-2"
			entireHeaderToggles
			bind:bodyShown={$cardsOpen.salesAndShipping}
		>
			<div class="form-row">
				<div class="col-md-4 col-xl-6">
					<Input
						id="shipping-weight"
						inputmode="decimal"
						type="number"
						class="text-right"
						title="The shipping weight of the item"
						label="Weight ({part.shippingDimensions.weightUnit?.toLowerCase()})"
						labelClass={field.weight.global ? 'text-underline' : ''}
						hint={getModifiedShippingDimensionHint('weight')}
						disabled={shouldDisable('weight')}
						bind:value={part.shippingDimensions.weight}
					/>
				</div>
				<div class="col-md-4 col-xl-6">
					<Input
						id="shipping-length"
						inputmode="decimal"
						type="number"
						class="text-right"
						title="The length of the item for shipping calculations."
						label="Length ({part.shippingDimensions.measurementUnit?.toLowerCase()})"
						labelClass={field.shippingLength.global ? 'text-underline' : ''}
						hint={getModifiedShippingDimensionHint('length')}
						disabled={shouldDisable('shippingLength')}
						bind:value={part.shippingDimensions.length}
					/>
				</div>
				<div class="col-md-4 col-xl-6">
					<Input
						id="shipping-width"
						inputmode="decimal"
						type="number"
						class="text-right"
						title="The width of the item for shipping calculations."
						label="Width ({part.shippingDimensions.measurementUnit?.toLowerCase()})"
						labelClass={field.shippingWidth.global ? 'text-underline' : ''}
						hint={getModifiedShippingDimensionHint('width')}
						disabled={shouldDisable('shippingWidth')}
						bind:value={part.shippingDimensions.width}
					/>
				</div>
				<div class="col-md-4 col-xl-6">
					<Input
						id="shipping-height"
						inputmode="decimal"
						type="number"
						class="text-right"
						title="The height of the item for shipping calculations."
						label="Height ({part.shippingDimensions.measurementUnit?.toLowerCase()})"
						labelClass={field.shippingHeight.global ? 'text-underline' : ''}
						hint={getModifiedShippingDimensionHint('height')}
						disabled={shouldDisable('shippingHeight')}
						bind:value={part.shippingDimensions.height}
					/>
				</div>
				<div class="col-md-4 col-xl-6">
					<Select
						id="weightUnit"
						label="Weight Unit"
						showEmptyOption={false}
						labelClass={field.weightUnit.global ? 'text-underline' : ''}
						hint={getModifiedShippingDimensionHint('weightUnit')}
						disabled={shouldDisable('weightUnit')}
						bind:value={part.shippingDimensions.weightUnit}
					>
						<option value="LB">Pounds</option>
						<option value="KG">Kilograms</option>
					</Select>
				</div>
				<div class="col-md-4 col-xl-6">
					<Select
						id="measurementUnit"
						label="Measurement Unit"
						showEmptyOption={false}
						labelClass={field.shippingMeasurementUnit.global ? 'text-underline' : ''}
						hint={getModifiedShippingDimensionHint('measurementUnit')}
						disabled={shouldDisable('shippingMeasurementUnit')}
						bind:value={part.shippingDimensions.measurementUnit}
					>
						<option value="IN">Inches</option>
						<option value="CM">Centimeters</option>
					</Select>
				</div>
			</div>
		</Card>
	</div>
</div>

<LoadItemModal
	itemTitle="Vehicles by Make, Model, Stock #, and VIN"
	itemIdProp="id"
	itemDisplayProp="stockNumber"
	doSearch={doVehicleSearch}
	on:lookupError={({ detail: { error } }) =>
		mediator.publish('showMessage', {
			heading: 'Error Looking Up Vehicles',
			message: error.message,
			color: 'danger',
			time: false,
		})}
	on:chooseItem={({ detail: { item } }) => {
		part.vehicleId = item.id
		part.vehicle = item
		// You can associate a vehicle to a REPLENISHABLE part, so don't override the stockCategory in that case
		part.stockCategory = part.replenishable ? 'REPLENISHABLE' : 'STANDARD'
		updatePricingAndAppraise()
	}}
	bind:this={vehicleLookupModal}
	let:item
>
	<div class="w-100 d-flex justify-content-between">
		<h5 class="mb-1">{item.stockNumber}</h5>
		<h5
			class="mb-1"
			title={item.store.name}
		>
			Store {item.store.code}
		</h5>
	</div>
	<div class="w-100">{item.year} {item.manufacturer?.name ?? ''} {item.model?.name ?? ''}</div>
	{#if item.vin}
		<small class="mt-1">VIN {item.vin}</small>
	{/if}
</LoadItemModal>

{#snippet modifiedHint(iconClass?: string)}
	<Icon
		class="text-warning {iconClass}"
		icon="pencil"
		size="xs"
	/>
{/snippet}
